import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBasket, Phone } from 'lucide-react';
import { navLinks, contact } from '../mock/mock';
import { useCart } from '../context/CartContext';
import logoDark from '../assets/logo-dark.png';
import logoCream from '../assets/logo-cream.png';

const headerLinks = navLinks.filter((l) => l.to !== '/faq' && l.to !== '/blog' && l.to !== '/careers');

export default function Navbar({ onNewsletter, onOrder, onPickup }) {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = scrolled || !isHome || open;
  const textDark = solid || isHome;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid
          ? 'bg-[#E9EBED]/95 backdrop-blur-md shadow-[0_1px_0_rgba(44,58,40,0.08)] py-3'
          : 'bg-transparent py-5'
      }`}
    >
      {/* DESKTOP: centered logo with menu below */}
      <div className="hidden lg:block max-w-7xl mx-auto px-5 sm:px-8">
        <div className="relative flex items-center justify-center">
          <div className="absolute left-0 flex items-center">
            <a
              href={`tel:${contact.phone.replace(/\s+/g, '')}`}
              data-testid="header-call-btn"
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                textDark ? 'bg-[#2C3A28] text-[#EDEAE0] hover:bg-[#1f2a1c]' : 'bg-white/90 text-[#2C3A28] hover:bg-white'
              }`}
            >
              <Phone size={16} strokeWidth={2.5} />
              Call Us
            </a>
          </div>
          <Link to="/" className="group" aria-label="Al Naturale home">
            <img
              src={textDark ? logoDark : logoCream}
              alt="Al Naturale"
              className={`w-auto transition-all duration-300 h-24 xl:h-28 ${!solid ? 'brightness-[1.18] contrast-[1.15] drop-shadow-[0_3px_14px_rgba(0,0,0,0.55)]' : ''}`}
            />
          </Link>
          <div className="absolute right-0 flex items-center gap-2.5">
            <button
              onClick={onOrder}
              data-testid="order-now-btn"
              className="inline-flex items-center whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold bg-[#E9E5D8] text-[#2C3A28] transition-all duration-300 hover:-translate-y-0.5"
            >
              Order Online
            </button>
            <button
              onClick={onPickup}
              data-testid="instore-pickup-btn"
              className="hidden xl:inline-flex items-center rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 bg-[#E9EBED] text-[#2C3A28] border border-[#2C3A28]/30"
            >
              In Store Pick Up
            </button>
            <Link
              to="/products#pickup-box"
              data-testid="pickup-basket-btn"
              aria-label="In store pick up box"
              className={`relative inline-flex items-center justify-center w-11 h-11 rounded-full transition-colors ${
                textDark ? 'text-[#2C3A28] hover:bg-[#EDEAE0]' : 'text-[#E9EBED] hover:bg-white/15'
              }`}
            >
              <ShoppingBasket size={20} />
              {count > 0 && (
                <span
                  data-testid="pickup-basket-count"
                  className="absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-[#2C3A28] text-[#E9EBED] text-[11px] font-bold flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </Link>
          </div>
        </div>
        <nav className="mt-4 flex items-center justify-center gap-6 xl:gap-8">
          {headerLinks.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`relative text-[13px] xl:text-[14px] font-light tracking-wide whitespace-nowrap transition-colors ${
                  solid ? 'text-[#2C3A28]/80 hover:text-[#2C3A28]' : 'text-white hover:text-white'
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1.5 left-0 h-[2px] bg-[#5A5D5F] transition-all duration-300 ${
                    active ? 'w-full' : 'w-0'
                  }`}
                />
              </Link>
            );
          })}
        </nav>
      </div>

      {/* MOBILE: logo row, then action buttons under logo */}
      <div className="lg:hidden max-w-7xl mx-auto px-4 sm:px-8">
        <div className="flex items-center justify-between gap-2">
          <a
            href={`tel:${contact.phone.replace(/\s+/g, '')}`}
            data-testid="header-call-btn-mobile"
            className={`inline-flex items-center gap-1.5 shrink-0 rounded-full px-3 py-2 text-xs font-semibold transition-colors ${
              textDark ? 'bg-[#2C3A28] text-[#EDEAE0]' : 'bg-white/90 text-[#2C3A28]'
            }`}
          >
            <Phone size={14} strokeWidth={2.5} />
            Call Us
          </a>
          <Link to="/" className="group shrink-0" aria-label="Al Naturale home">
            <img
              src={textDark ? logoDark : logoCream}
              alt="Al Naturale"
              className={`h-12 sm:h-16 w-auto transition-opacity duration-300 ${!solid ? 'brightness-[1.18] contrast-[1.15] drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]' : ''}`}
            />
          </Link>
          <div className="flex items-center gap-0.5 shrink-0">
            <Link
              to="/products#pickup-box"
              data-testid="pickup-basket-btn-mobile"
              aria-label="In store pick up box"
              className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors ${
                textDark ? 'text-[#2C3A28] hover:bg-[#EDEAE0]' : 'text-[#E9EBED] hover:bg-white/15'
              }`}
            >
              <ShoppingBasket size={19} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-[#2C3A28] text-[#E9EBED] text-[11px] font-bold flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
            <button
              onClick={() => setOpen((o) => !o)}
              className={`inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors ${
                textDark ? 'text-[#2C3A28] hover:bg-[#EDEAE0]' : 'text-[#E9EBED] hover:bg-white/15'
              }`}
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* mobile menu */}
      <div
        className={`lg:hidden overflow-y-auto transition-all duration-400 ${
          open ? 'max-h-[80vh] mt-3' : 'max-h-0'
        }`}
      >
        <nav className="px-5 sm:px-8 pb-5 flex flex-col gap-1">
          {headerLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`py-3 px-4 rounded-xl text-[17px] font-medium font-display transition-colors ${
                location.pathname === l.to
                  ? 'bg-[#EDEAE0] text-[#2C3A28]'
                  : 'text-[#2C3A28] hover:bg-[#EDEAE0]'
              }`}
            >
              {l.label}
            </Link>
          ))}
          <button
            onClick={onOrder}
            data-testid="order-now-btn-mobile"
            className="mt-2 py-3 rounded-full bg-[#E9E5D8] text-[#2C3A28] font-semibold"
          >
            Order Online
          </button>
          <button
            onClick={onPickup}
            data-testid="instore-pickup-btn-mobile"
            className="mt-2 py-3 rounded-full bg-[#E9EBED] text-[#2C3A28] border border-[#2C3A28]/30 font-semibold"
          >
            In Store Pick Up
          </button>
        </nav>
      </div>
    </header>
  );
}
